/**
 * Composable Vue.js para cálculos automáticos de porcentagens
 *
 * Este composable centraliza toda a lógica de cálculo de porcentagens
 * para atividades e subtarefas, seguindo as regras de negócio especificadas.
 */

import { computed } from 'vue'

export function usePercentageCalculations() {

  /**
   * Calcula a porcentagem baseada no status
   * @param {string} status - Status da atividade/subtarefa
   * @returns {number} Porcentagem correspondente ao status
   */
  const getPercentageByStatus = (status) => {
    const statusPercentages = {
      'not_started': 0,
      'in_progress': 50,
      'completed': 100,
      'overdue': 0 // Consideramos como não iniciada para cálculo
    }

    return statusPercentages[status] || 0
  }

  /**
   * Calcula a porcentagem de uma subtarefa considerando sua contribuição para a atividade principal
   * @param {Object} subtask - Objeto da subtarefa
   * @param {number} totalSubtasks - Total de subtarefas na atividade principal
   * @returns {number} Porcentagem da subtarefa
   */
  const calculateSubtaskPercentage = (subtask, totalSubtasks) => {
    if (totalSubtasks === 0) return 0

    const statusPercentage = getPercentageByStatus(subtask.status)
    const contributionPercentage = (1 / totalSubtasks) * 100

    return Math.round((statusPercentage / 100) * contributionPercentage)
  }

  /**
   * Calcula a porcentagem total de uma atividade baseada em suas subtarefas
   * @param {Object} activity - Objeto da atividade
   * @returns {number} Porcentagem total da atividade
   */
  const calculateActivityPercentage = (activity) => {

    if (!activity.subtasks || activity.subtasks.length === 0) {
      return getPercentageByStatus(activity.status)
    }

    const totalSubtasks = activity.subtasks.length
    let totalWeightedPercentage = 0

    activity.subtasks.forEach(subtask => {

      const subtaskCalculatedPercentage = calculateActivityPercentage(subtask)
      const contributionWeight = 1 / totalSubtasks
      totalWeightedPercentage += (subtaskCalculatedPercentage / 100) * contributionWeight
    })

    return Math.round(totalWeightedPercentage * 100)
  }

  /**
   * Cria computed properties para uma atividade específica
   * @param {Object} activity - Reactive object da atividade
   * @returns {Object} Objeto com computed properties para porcentagens
   */
  const createActivityPercentageComputeds = (activity) => {
    const calculatedProgress = computed(() => {
      return calculateActivityPercentage(activity.value || activity)
    })

    const calculatedTotalSubtasks = computed(() => {
      const act = activity.value || activity
      return act.subtasks ? act.subtasks.length : 0
    })

    const calculatedCompletedSubtasks = computed(() => {
      const act = activity.value || activity
      if (!act.subtasks) return 0
      return act.subtasks.filter(subtask => subtask.status === 'completed').length
    })

    const subtasksProgressPercentage = computed(() => {
      const total = calculatedTotalSubtasks.value
      const completed = calculatedCompletedSubtasks.value
      if (total === 0) return 0
      return Math.round((completed / total) * 100)
    })

    const getCalculatedSubtaskPercentage = (subtask) => {
      const totalSubtasks = calculatedTotalSubtasks.value
      return calculateSubtaskPercentage(subtask, totalSubtasks)
    }

    return {
      calculatedProgress,
      calculatedTotalSubtasks,
      calculatedCompletedSubtasks,
      subtasksProgressPercentage,
      getCalculatedSubtaskPercentage
    }
  }

  /**
   * Atualiza automaticamente as porcentagens de uma atividade e suas subtarefas
   * @param {Object} activity - Objeto da atividade
   * @returns {Object} Atividade com porcentagens atualizadas
   */
  const updateActivityPercentages = (activity) => {
    const updatedActivity = { ...activity }

    if (updatedActivity.subtasks && updatedActivity.subtasks.length > 0) {
      const totalSubtasks = updatedActivity.subtasks.length

      updatedActivity.subtasks = updatedActivity.subtasks.map(subtask => ({
        ...subtask,
        percentage: calculateSubtaskPercentage(subtask, totalSubtasks)
      }))

      updatedActivity.totalSubtasks = totalSubtasks
      updatedActivity.completedSubtasks = updatedActivity.subtasks.filter(
        subtask => subtask.status === 'completed'
      ).length
    }

    updatedActivity.progress = calculateActivityPercentage(updatedActivity)

    return updatedActivity
  }

  /**
   * Atualiza as porcentagens de todas as atividades em uma lista
   * @param {Array} activities - Lista de atividades
   * @returns {Array} Lista de atividades com porcentagens atualizadas
   */
  const updateAllActivitiesPercentages = (activities) => {
    return activities.map(activity => updateActivityPercentages(activity))
  }

  /**
   * Calcula estatísticas de progresso para um conjunto de atividades
   * @param {Array} activities - Lista de atividades
   * @returns {Object} Estatísticas de progresso
   */
  const calculateProgressStats = (activities) => {
    if (!activities || activities.length === 0) {
      return {
        totalActivities: 0,
        completedActivities: 0,
        inProgressActivities: 0,
        notStartedActivities: 0,
        overallProgress: 0
      }
    }

    const stats = {
      totalActivities: activities.length,
      completedActivities: 0,
      inProgressActivities: 0,
      notStartedActivities: 0,
      overallProgress: 0
    }

    let totalProgress = 0

    activities.forEach(activity => {
      const progress = calculateActivityPercentage(activity)
      totalProgress += progress

      if (progress === 100) {
        stats.completedActivities++
      } else if (progress > 0) {
        stats.inProgressActivities++
      } else {
        stats.notStartedActivities++
      }
    })

    stats.overallProgress = Math.round(totalProgress / activities.length)

    return stats
  }

  /**
   * Valida se uma atividade tem a estrutura correta para cálculos
   * @param {Object} activity - Objeto da atividade
   * @returns {boolean} True se a atividade é válida
   */
  const validateActivityStructure = (activity) => {
    if (!activity || typeof activity !== 'object') {
      return false
    }

    const validStatuses = ['not_started', 'in_progress', 'completed', 'overdue']
    if (!validStatuses.includes(activity.status)) {
      return false
    }

    if (activity.subtasks && Array.isArray(activity.subtasks)) {
      return activity.subtasks.every(subtask =>
        subtask &&
        typeof subtask === 'object' &&
        validStatuses.includes(subtask.status)
      )
    }

    return true
  }

  return {

    getPercentageByStatus,
    calculateSubtaskPercentage,
    calculateActivityPercentage,

    createActivityPercentageComputeds,

    updateActivityPercentages,
    updateAllActivitiesPercentages,

    calculateProgressStats,

    validateActivityStructure
  }
}
